import {
  BenefitVoucher,
  DiscoveryFlashSale,
  DiscoveryPromotion,
  PromotionCondition,
} from '@teko/discovery';
import { CartOrderLine } from '@teko/cart';

// export again for backward compatible
export type {
  Cart,
  AppliedServices,
  DisplayPolicy,
  RequiredParams,
  DeliveryMethod,
  AddToCartReq,
  EVoucher,
  ApplyCouponReq,
  UpdateOrderLineReq,
  ShippingInfo,
  CheckoutBody,
  CartOrderLine,
  ChildOrderLine,
  InvoiceInfo,
  SellerNote,
  CheckoutApplicableService,
} from '@teko/cart';

export type { PaymentMethod, PaymentDeposit } from '@teko/payment';

export type CouponInfoCondition = PromotionCondition & {
  skus: string[];
};

export type CouponInfo = Omit<DiscoveryPromotion, 'condition'> & {
  id: number;
  endedAt: string;
  applyOn?: string;
  type: string;
  name: string;
  description: string;
  sellerId: number;
  condition: CouponInfoCondition | null;
  isPrivate?: boolean;
  isDefault?: boolean;
  applySellerIds?: number[];
  benefit: CartOrderCouponBenefit;
};

export interface CartOrderCoupons {
  applicableCoupons: CouponInfo[];
  inapplicableCoupons: CouponInfo[];
}

export interface CartContextStateAppliedCoupon {
  text: string;
  type: string;
  promotionId: string;
  discount: number;
  sellerId: number;
  applyOn: string;
}

export interface CartContextStateSuggestedCoupon {
  text: string;
  discount?: number;
  detail?: string;
  disabled?: boolean;
  href?: string;
  type: string;
  promotionId: string;
  action?: (coupon: string, value: number) => void;
  hasCondition?: boolean;
  orderValueMin?: number;
  orderValueMax?: number;
  benefit: PartOrderBenefit;
  skus?: Array<string>;
  sellerId?: number;
  applyOn: string;
  endedAt?: Date;
  applySellerIds: Array<number>;
}

export interface PartOrderPromotion {
  sellerId?: number;
  promotionId: string;
  disabled: boolean;
  coupon: string;
  orderValueMin: number;
  orderValueMax: number;
  benefit: PartOrderBenefit;
}

export interface PartOrderBenefit {
  discount?: PartOrderDiscount;
  voucher?: PartOrderVoucher;
  gifts?: Array<PartOrderGifts>;
}

export interface PartOrderDiscount {
  percent?: number;
  maxAmount?: number;
  maxAmountPerOrder?: number;
  budgetStatus?: string;
  outOfBudget?: boolean;
  flat?: number;
}

export interface PartOrderVoucher {
  name: string;
  quantity: number;
  maxQuantity: number;
}

export interface PartOrderGifts {
  sku: string;
  name: string;
  imageUrl: string;
  quantity: number;
  maxQuantityPerOrder?: string | number;
}

export interface CartSeller {
  id: number;
  logo: string;
  displayName: string;
  name?: string;
}

export interface CartSellerLine {
  grandTotal: number;
  orderLines: CartOrderLine[];
  promotions: CartPromotion[];
  seller: CartSeller;
  selectedCount: number;
  sellerId: number;
  totalCount: number;
  totalPriceDifference: number;
}

export interface CartOrderCouponBenefitDiscount {
  flat?: number;
  maxAmount: number;
  maxAmountPerOrder?: number;
  percent?: number;
}

export interface CartOrderCouponBenefit {
  discount?: CartOrderCouponBenefitDiscount;
  voucher?: BenefitVoucher;
  gifts: CartGift[];
}

export interface CartOrderCouponCondition {
  blockSize: number;
  coupon?: string;
  minQuantity: number;
  orderValueMax?: number;
  orderValueMin?: number;
  remain: number;
}

export interface CartOrderCoupon {
  applyOn: string;
  applySellerIds?: number[];
  benefit: CartOrderCouponBenefit;
  condition: CartOrderCouponCondition;
  description?: string;
  endedAt: string;
  govRegister?: boolean;
  id: number;
  isDefault: boolean;
  isPrivate?: boolean;
  name: string;
  partner?: string;
  sellerApplyType?: string;
  sellerId: number;
  startedAt: string;
  type: string;
}

export interface CartPromotionApplyOn {
  lineItemId: string;
  quantity: number;
  serviceId: number;
}

export interface CartGift {
  sku: string;
  name: string;
  quantity: number;
  isDefault?: boolean;
  imageUrl?: string;
}

export interface CartPromotion {
  applyOn?: CartPromotionApplyOn[];
  applyType: string;
  baseFlashSale?: DiscoveryFlashSale;
  basePromotion?: DiscoveryPromotion;
  coupon?: string;
  discount?: number;
  id: number;
  isSelected: boolean;
  promotionId: string;
  quantity: number;
  sellerId: number;
  type: string;
  gifts?: CartGift[];
  voucher?: BenefitVoucher;
  sellerIds?: number[];
  isDefault?: boolean;
}

export interface OtherFee {
  code: string;
  discountAmount: number;
  groupId: number;
  id: number;
  name: string;
  price: number;
  rowTotal: number;
  sellerId: number;
}

export enum AddToCartType {
  ADDTOCART = 'addToCart',
  BUYNOW = 'buyNow',
}

export interface CartContextStateAppliedEVoucher {
  text: string;
  applySellerIds: number[];
  endedAt?: Date;
  discount: number;
}

export interface ILocationProvince {
  code: string;
  name: string;
  region: string;
}
